import React from 'react';
import { Link } from 'react-router-dom';
import { Box, IconButton, InputAdornment, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { Monogram } from '../navigation/SidebarNav';
import { DISPLAY_FONT, PREMIUM_COLORS, tokens } from '../../theme';

const sb = PREMIUM_COLORS.sidebar;

/** Znak Visitio: monogram i nazwa w kroju szeryfowym. `tone="dark"` na ciemnym tle. */
export const VisitioWordmark: React.FC<{ tone?: 'light' | 'dark'; size?: number }> = ({ tone = 'light', size = 36 }) => (
  <Stack component={Link} to="/" direction="row" alignItems="center" gap={1.25} aria-label="Visitio — strona główna" sx={{ textDecoration: 'none', width: 'fit-content' }}>
    <Monogram size={size} />
    <Typography
      component="span"
      sx={{
        fontFamily: DISPLAY_FONT,
        fontWeight: 600,
        fontSize: '1.375rem',
        letterSpacing: '-0.01em',
        lineHeight: 1,
        fontVariationSettings: '"opsz" 48',
        color: tone === 'dark' ? tokens.paper : tokens.ink,
      }}
    >
      Visitio
    </Typography>
  </Stack>
);

interface AuthLayoutProps {
  /** Tytul formularza (h1). */
  title: React.ReactNode;
  /** Jedno zdanie pod tytulem. */
  description?: React.ReactNode;
  /** Mala linia nad tytulem, np. nazwa salonu przy zaproszeniu. */
  eyebrow?: React.ReactNode;
  /** Zdanie w ciemnym panelu po lewej. */
  tagline?: React.ReactNode;
  /** Ikona stanu nad tytulem (np. nieaktywne zaproszenie). */
  icon?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Wspolny uklad logowania, rejestracji i zaproszenia: ciemny panel marki po lewej
 * (tylko od tabletu w poziomie), jasny formularz po prawej.
 */
export const AuthLayout: React.FC<AuthLayoutProps> = ({ title, description, eyebrow, tagline, icon, children }) => (
  <Box
    sx={{
      minHeight: '100vh',
      display: 'grid',
      gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 5fr) minmax(0, 6fr)' },
      bgcolor: tokens.paper,
    }}
  >
    <Box
      component="aside"
      sx={{
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        justifyContent: 'space-between',
        position: 'sticky',
        top: 0,
        height: '100vh',
        px: { md: 6, lg: 8 },
        py: { md: 6, lg: 7 },
        bgcolor: tokens.aubergine,
        // Ta sama, ledwie widoczna poswiata co w pasku bocznym aplikacji.
        backgroundImage: `radial-gradient(120% 55% at 0% 0%, ${alpha(tokens.champagne, 0.1)}, transparent 55%)`,
        color: sb.text,
      }}
    >
      <VisitioWordmark tone="dark" />
      <Box sx={{ maxWidth: 440 }}>
        <Box sx={{ width: 40, height: '1px', bgcolor: alpha(tokens.champagne, 0.6), mb: 3 }} />
        <Typography sx={{ fontSize: '1.375rem', fontWeight: 500, lineHeight: 1.5, color: alpha(tokens.paper, 0.92), textWrap: 'pretty' } as object}>
          {tagline || 'Kalendarz, klienci, zespół i rezerwacje online salonu beauty — w jednym, spokojnym miejscu.'}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: '0.8125rem', color: sb.textDim }}>System dla salonów beauty</Typography>
    </Box>

    <Box
      component="main"
      sx={{
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'center',
        px: { xs: 2.5, sm: 4 },
        py: { xs: 4, sm: 6 },
        minWidth: 0,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 420 }}>
        <Box sx={{ display: { xs: 'block', md: 'none' }, mb: { xs: 4, sm: 5 } }}>
          <VisitioWordmark />
        </Box>
        {icon}
        {eyebrow && (
          <Typography component="div" sx={{ color: tokens.muted, fontWeight: 600, fontSize: '0.8125rem', mb: 0.75 }}>
            {eyebrow}
          </Typography>
        )}
        <Typography variant="h1" sx={{ textWrap: 'balance' } as object}>{title}</Typography>
        {description && (
          <Typography component="div" sx={{ mt: 1, color: tokens.inkSoft, fontSize: '0.9375rem' }}>
            {description}
          </Typography>
        )}
        <Box sx={{ mt: { xs: 3, sm: 3.5 } }}>{children}</Box>
      </Box>
    </Box>
  </Box>
);

/** Przycisk pokazania hasla w polu. */
export const PasswordToggle: React.FC<{ visible: boolean; onToggle: () => void }> = ({ visible, onToggle }) => (
  <InputAdornment position="end">
    <IconButton onClick={onToggle} edge="end" aria-label={visible ? 'Ukryj hasło' : 'Pokaż hasło'}>
      {visible ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
    </IconButton>
  </InputAdornment>
);
